import {
  IconButton,
  Box,
  AppBar,
  useMediaQuery,
  Toolbar,
  styled,
  Stack,
  Chip,
  Typography,
  Modal,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';

import config from '@/context/config';
import { useContext, useState, useEffect, useCallback } from 'react';

import { IconMenu2, IconMoon, IconSun, IconX } from '@tabler/icons-react';
import Notifications from './Notification';
import Profile from './Profile';
import Language from './Language';
// import Navigation from './Navigation';
import { CustomizerContext } from '@/context/CustomizerContext';
import { useAuth } from '@/context/AuthContext';
import { apiService } from '@/services/api';
import CreateMenu from './CreateMenu';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const lgUp = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const lgDown = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  const navigate = useNavigate();

  const TopbarHeight = config.topbarHeight;

  // drawer
  const {
    activeMode,
    setActiveMode,
    setIsCollapse,
    isCollapse,
    isMobileSidebar,
    setIsMobileSidebar,
  } = useContext(CustomizerContext);

  // auth
  const { user } = useAuth();

  // online status
  const [isOnline, setIsOnline] = useState(false);
  const [nextWorkingTime, setNextWorkingTime] = useState<string>('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState<any>(null);
  const [balance, setBalance] = useState<number | null>(null);
  // const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);

  const getNextWorkingTime = (
    scheduleData: any,
    currentDay: number,
    currentTime: string
  ) => {
    const dayNames = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];

    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun',];

    // Перевіряємо наступні 7 днів
    for (let i = 0; i < 7; i++) {
      const dayIndex = (currentDay + i) % 7;
      const dayKey = dayNames[dayIndex] as keyof typeof scheduleData;
      const daySchedule = scheduleData[dayKey];

      if (daySchedule && !daySchedule.isClosed) {
        const startTime = daySchedule.start.slice(11, 16);

        // Якщо це сьогодні, перевіряємо чи час ще не пройшов
        if (i === 0) {
          const timeToMinutes = (time: string) => {
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
          };

          const currentMinutes = timeToMinutes(currentTime);
          const startMinutes = timeToMinutes(startTime);

          if (currentMinutes < startMinutes) {
            return `${dayLabels[dayIndex]} ${startTime}`;
          }
        } else {
          // Наступні дні - просто повертаємо час початку
          return `${dayLabels[dayIndex]} ${startTime}`;
        }
      }
    }

    return 'Closed';
  };

  useEffect(() => {
    const checkOnlineStatus = async () => {
      if (user?.userType === 'customer') {
        try {
          const scheduleData = await apiService.getSchedule();
          if (scheduleData) {
            setScheduleData(scheduleData);
            const now = new Date();
            const dayNames = [
              'sunday',
              'monday',
              'tuesday',
              'wednesday',
              'thursday',
              'friday',
              'saturday',
            ];
            const currentDay = now.getDay();
            const currentDayKey = dayNames[
              currentDay
            ] as keyof typeof scheduleData;
            const currentTime = now.toTimeString().slice(0, 5); // HH:mm format

            const daySchedule = scheduleData[currentDayKey];
            if (daySchedule && !daySchedule.isClosed) {
              // Витягуємо тільки час з ISO строки (ігноруємо дату)
              const startTime = daySchedule.start.slice(11, 16); // Extract HH:mm from ISO string
              const endTime = daySchedule.end.slice(11, 16); // Extract HH:mm from ISO string

              // Конвертуємо час в хвилини для порівняння
              const timeToMinutes = (time: string) => {
                const [hours, minutes] = time.split(':').map(Number);
                return hours * 60 + minutes;
              };

              const currentMinutes = timeToMinutes(currentTime);
              const startMinutes = timeToMinutes(startTime);
              const endMinutes = timeToMinutes(endTime);

              // Перевіряємо чи поточний час в межах робочого часу
              let isInWorkingHours = false;

              if (startMinutes <= endMinutes) {
                // Звичайний випадок: робочий час в межах одного дня (наприклад, 09:00-17:00)
                isInWorkingHours =
                  currentMinutes >= startMinutes &&
                  currentMinutes <= endMinutes;
              } else {
                // Випадок коли робочий час переходить через північ (наприклад, 22:00-06:00)
                isInWorkingHours =
                  currentMinutes >= startMinutes ||
                  currentMinutes <= endMinutes;
              }

              setIsOnline(isInWorkingHours);

              // Якщо офлайн, розраховуємо наступний робочий час
              if (!isInWorkingHours) {
                const nextTime = getNextWorkingTime(
                  scheduleData,
                  currentDay,
                  currentTime
                );
                setNextWorkingTime(nextTime);
              }
            } else {
              setIsOnline(false);
              const nextTime = getNextWorkingTime(
                scheduleData,
                currentDay,
                currentTime
              );
              setNextWorkingTime(nextTime);
            }
          }
        } catch (error) {
          console.error('Error checking online status:', error);
          setIsOnline(false);
        }
      }
    };

    checkOnlineStatus();
    // Check every minute
    const interval = setInterval(checkOnlineStatus, 60000);

    return () => clearInterval(interval);
  }, [user?.userType]);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await apiService.getBalance();
      setBalance(response.balance);
    } catch (error) {
      console.error('Failed to load balance:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.userType === 'customer') {
      fetchBalance();
      const handleRefresh = () => {
        fetchBalance();
      };

      window.addEventListener('balance:refresh', handleRefresh);
      return () => {
        window.removeEventListener('balance:refresh', handleRefresh);
      };
    }
  }, [fetchBalance, user?.userType]);

  const formatTime = (isoTime: string) => {
    return isoTime.slice(11, 16); // Extract HH:mm from ISO string
  };

  const getDayLabel = (dayKey: string) => {
    const dayLabels: { [key: string]: string } = {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
    };
    return dayLabels[dayKey] || dayKey;
  };

  // const handleCreateMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
  //   setCreateMenuAnchor(e.currentTarget);
  // }, []);
  //
  // const handleCreateMenuClose = useCallback(() => {
  //   setCreateMenuAnchor(null);
  // }, []);
  //
  // const handleCreateTicket = useCallback(() => {
  //   setCreateMenuAnchor(null);
  //   navigate('/app/tickets?openCreate=true');
  // }, [navigate]);
  //
  // const handleCreateFileService = useCallback(() => {
  //   setCreateMenuAnchor(null);
  //   navigate('/app/new-file-service');
  // }, [navigate]);

  const AppBarStyled = styled(AppBar)(({ theme }) => ({
    boxShadow: 'none',
    background: theme.palette.background.default,
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    [theme.breakpoints.up('lg')]: {
      minHeight: TopbarHeight,
    },
  }));
  const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
    width: '100%',
    color: theme.palette.text.secondary,
  }));

  return (
    <>
      <AppBarStyled position="sticky" color="transparent">
        <ToolbarStyled>
          {/* ------------------------------------------- */}
          {/* Toggle Button Sidebar */}
          {/* ------------------------------------------- */}
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={() => {
              // Toggle sidebar on both mobile and desktop based on screen size
              if (lgUp) {
                // For large screens, toggle between full-sidebar and mini-sidebar
                isCollapse === 'full-sidebar'
                  ? setIsCollapse('mini-sidebar')
                  : setIsCollapse('full-sidebar');
              } else {
                // For smaller screens, toggle mobile sidebar
                setIsMobileSidebar(!isMobileSidebar);
              }
            }}
          >
            <IconMenu2 size="20" />
          </IconButton>

          {/* Online Status Badge for Customers */}
          {user?.userType === 'customer' && !lgDown && (
            <>
              {!lgDown && <Typography ml={4}>File service</Typography>}
              <Chip
                label={isOnline ? 'Online' : 'Offline'}
                color={isOnline ? 'success' : 'error'}
                size="small"
                sx={{ ml: 1, cursor: 'pointer' }}
                onClick={() => setScheduleModalOpen(true)}
              />
              {!isOnline && (
                <>
                  <Typography ml={1}>until</Typography>
                  <Chip
                    label={nextWorkingTime}
                    color="default"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </>
              )}
            </>
          )}

          {/* ------------------------------------------- */}
          {/* Search Dropdown */}
          {/* ------------------------------------------- */}
          {/*<Search />*/}
          {/*{lgUp ? (*/}
          {/*  <>*/}
          {/*    <Navigation />*/}
          {/*  </>*/}
          {/*) : null}*/}

          <Box flexGrow={1} />
          <Stack spacing={1} direction="row" alignItems="center">
            {balance !== null && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  navigate('/shop');
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {lgDown ? 'Cr:' : 'Credits:'}
                </Typography>
                <Chip label={balance} color="primary" size="small" />
              </Stack>
            )}
            <CreateMenu />
            <Language />
            {/* ------------------------------------------- */}
            {/* Ecommerce Dropdown */}
            {/* ------------------------------------------- */}
            {/* ------------------------------------------- */}
            {/* End Ecommerce Dropdown */}
            {/* ------------------------------------------- */}
            <IconButton
              size="large"
              color="inherit"
              onClick={() =>
                setActiveMode(activeMode === 'light' ? 'dark' : 'light')
              }
            >
              {activeMode === 'light' ? (
                <IconMoon size="21" stroke="1.5" />
              ) : (
                <IconSun size="21" stroke="1.5" />
              )}
            </IconButton>
            {/*<Notifications />*/}
            <Notifications />
            <Profile />
          </Stack>
        </ToolbarStyled>
      </AppBarStyled>

      {/* Schedule Modal */}
      <Modal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        aria-labelledby="schedule-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '500px' },
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Card>
            <CardHeader
              title="Working Hours"
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
              action={
                <IconButton
                  aria-label="close"
                  onClick={() => setScheduleModalOpen(false)}
                  sx={{ color: 'text.secondary' }}
                >
                  <IconX size="20" />
                </IconButton>
              }
            />
            <CardContent>
              {scheduleData ? (
                <Box>
                  {(() => {
                    // Define correct day order: Monday to Sunday
                    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    
                    // Sort entries by day order
                    const sortedEntries = Object.entries(scheduleData).sort(
                      ([dayKeyA], [dayKeyB]) => {
                        const indexA = dayOrder.indexOf(dayKeyA);
                        const indexB = dayOrder.indexOf(dayKeyB);
                        return indexA - indexB;
                      }
                    );

                    return sortedEntries.map(
                      ([dayKey, daySchedule]: [string, any]) => {
                        if (
                          typeof daySchedule === 'object' &&
                          daySchedule.start &&
                          daySchedule.end
                        ) {
                          return (
                            <Box key={dayKey} sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="medium"
                                >
                                  {getDayLabel(dayKey)}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  {daySchedule.isClosed ? (
                                    <Chip
                                      label="Closed"
                                      color="error"
                                      size="small"
                                    />
                                  ) : (
                                    <>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {formatTime(daySchedule.start)} -{' '}
                                        {formatTime(daySchedule.end)}
                                      </Typography>
                                      <Chip
                                        label="Open"
                                        color="success"
                                        size="small"
                                      />
                                    </>
                                  )}
                                </Box>
                              </Box>
                              {dayKey !== 'sunday' && (
                                <Divider sx={{ mt: 1 }} />
                              )}
                            </Box>
                          );
                        }
                        return null;
                      }
                    );
                  })()}
                </Box>
              ) : (
                <Typography>Loading schedule...</Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </>
  );
};

export default Header;
